// web-v2/src/lib/v3-classic.js
//
// "OPEN IN CLASSIC": WHICH V2 SCREEN MATCHES THE V3 SCREEN YOU ARE ON (V3
// Overnight 3, lane C). One table, one function. Every v3 route in router.jsx
// has its row here, and tests/v3-front-door.test.js fails the gate on a v3
// route without one (and on a row with no route), so a new v3 screen cannot
// ship without saying where its classic twin is.
//
// A row answers { href } or { href: null, reason }: a v3 screen whose subject
// has no V2 twin (an account with no CRM id, which the V2 workspace cannot
// open) says so in words; it never links to a screen that will not open it.
// Pure: no imports, so the guard runs it in node. The ids come from the
// payload the page already read; nothing here fetches or derives a state.

export const NO_CLASSIC_WORDS = Object.freeze({
  no_crm_account: 'No classic screen: this account has no CRM id',
  not_loaded: 'Classic link waits for the account to load',
});

// "OPEN DEAL": the classic deal workspace (where the quote entry and the rater
// live) for one CRM deal, by the same V2 route the account link uses
// (account/:idOrSlug/deal/:dealId). The ONE place that builds it: every v3 term
// block, every pending term and the account link's ?term= focus call it, so no
// screen spells the classic URL itself. A term with no CRM deal id, or an
// account with no CRM id, says so in words and links nowhere.
export const NO_DEAL_WORDS = Object.freeze({
  no_crm_account: 'No classic deal: this account has no CRM id',
  no_crm_deal: 'No classic deal: this term has no CRM deal id',
});
export function classicDealHref(account, crmDealId) {
  if (!account || !account.crm_account_id) return { href: null, reason: 'no_crm_account' };
  if (!crmDealId) return { href: null, reason: 'no_crm_deal' };
  return { href: `/account/${encodeURIComponent(account.crm_account_id)}/deal/${encodeURIComponent(crmDealId)}` };
}

// v3 route pattern (exactly as router.jsx spells it, leading slash added) ->
// (params, query, payload) => { href, label? } | { href: null, reason }
export const CLASSIC_ROUTES = Object.freeze({
  // The account page. V2 opens an account by its CRM id (routes/account.jsx,
  // account/:idOrSlug); a ?term=<policy id> focus opens that term's deal
  // workspace when the term carries its CRM deal id, else the account.
  // NO PAYLOAD IS NOT NO WAY OUT (lane C review C-R4): while the page loads, or
  // when the read failed (503 store_unavailable before migration 11, a stale
  // id, the flag turned off under a bookmark), a URL id that is a CRM id opens
  // that account in V2 directly, and a uuid opens the classic search.
  '/v3/account/:id': (params, query, payload) => {
    const account = payload && payload.account;
    if (!account) {
      return /^\d+$/.test(String(params.id || ''))
        ? { href: `/account/${encodeURIComponent(params.id)}` }
        : { href: '/search', label: 'Search in classic' };
    }
    if (!account.crm_account_id) return { href: null, reason: 'no_crm_account' };
    const base = `/account/${encodeURIComponent(account.crm_account_id)}`;
    const termId = query && query.get ? query.get('term') : null;
    const term = termId ? (((payload.terms || []).find((t) => t.id === termId)) || ((payload.pending_terms || []).find((t) => t.id === termId)) || null) : null;
    const deal = term && term.crm_deal_id ? classicDealHref(account, term.crm_deal_id) : null;
    return { href: deal && deal.href ? deal.href : base };
  },
});

function matchPattern(pattern, pathname) {
  const want = pattern.split('/').filter(Boolean);
  const got = String(pathname || '').split('?')[0].split('/').filter(Boolean);
  if (want.length !== got.length) return null;
  const params = {};
  for (let i = 0; i < want.length; i += 1) {
    if (want[i].startsWith(':')) params[want[i].slice(1)] = decodeURIComponent(got[i]);
    else if (want[i] !== got[i]) return null;
  }
  return params;
}

// { pattern, href, reason } for a v3 pathname, or null when the path is not
// a v3 route this table knows (the guard keeps that set equal to the router's).
export function classicFor(pathname, search, payload) {
  for (const [pattern, build] of Object.entries(CLASSIC_ROUTES)) {
    const params = matchPattern(pattern, pathname);
    if (!params) continue;
    const query = new URLSearchParams(search || '');
    const out = build(params, query, payload);
    return { pattern, href: out.href || null, reason: out.href ? null : (out.reason || 'not_loaded'), label: (out.href && out.label) || null };
  }
  return null;
}
